import React, { useState, useEffect } from "react";
import { useAuth } from "./AuthContext";
import { formatGameName } from "./utils/gameUtils";

const BACKEND_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const LicenseTable = () => {
  const { token } = useAuth();
  const [licenses, setLicenses] = useState([]);
  const [editedLicenses, setEditedLicenses] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionLoading, setActionLoading] = useState({});
  const [saveLoading, setSaveLoading] = useState(false);

  useEffect(() => {
    fetchLicenses();
  }, []);

  const fetchLicenses = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/admin/licenses`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch licenses");
      }

      const data = await response.json();
      setLicenses(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (licenseId, currentStatus) => {
    setActionLoading((prev) => ({ ...prev, [licenseId]: true }));
    try {
      const response = await fetch(`${BACKEND_URL}/admin/licenses/${licenseId}/status`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ active: !currentStatus }),
      });

      if (!response.ok) {
        throw new Error("Failed to toggle license status");
      }

      const data = await response.json();
      setLicenses((prev) =>
        prev.map((item) =>
          item.id === licenseId ? { ...item, active: data.license.active } : item
        )
      );
    } catch (err) {
      alert(`Error updating status: ${err.message}`);
    } finally {
      setActionLoading((prev) => ({ ...prev, [licenseId]: false }));
    }
  };

  const handleEdit = (id, field, value) => {
    setEditedLicenses((prev) => ({
      ...prev,
      [id]: { ...prev[id], [field]: value },
    }));

    setLicenses((prevLicenses) =>
      prevLicenses.map((license) =>
        license.id === id ? { ...license, [field]: value } : license
      )
    );
  };

  const handleSaveChanges = async () => {
    setSaveLoading(true);
    try {
      const entries = Object.entries(editedLicenses);
      for (const [id, changes] of entries) {
        const response = await fetch(`${BACKEND_URL}/admin/licenses/${id}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(changes),
        });

        if (!response.ok) {
          throw new Error(`Failed to update license ${id}`);
        }
      }

      setEditedLicenses({});
      await fetchLicenses();
      alert("License changes saved successfully!");
    } catch (err) {
      console.error("Error updating licenses:", err);
      alert(`Error saving changes: ${err.message}`);
    } finally {
      setSaveLoading(false);
    }
  };

  if (loading) return <p style={{ textAlign: "center", padding: "20px" }}>Loading licenses...</p>;
  if (error) return <p style={{ color: "red", textAlign: "center" }}>Error: {error}</p>;

  return (
    <div className="licenses">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <h1 className="title-blue" style={{ margin: 0 }}>System Licenses ({licenses.length})</h1>
        {Object.keys(editedLicenses).length > 0 && (
          <button onClick={handleSaveChanges} className="save-button" disabled={saveLoading}>
            {saveLoading ? "Saving Changes..." : "Save Changes"}
          </button>
        )}
      </div>

      <table className="license-table">
        <thead>
          <tr>
            <th>Purchaser</th>
            <th>License PIN</th>
            <th>Game Name</th>
            <th>Platform</th>
            <th>Plays (Used / Total)</th>
            <th>Expiration Date</th>
            <th>Status</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {licenses.length === 0 ? (
            <tr>
              <td colSpan="8" style={{ textAlign: "center", padding: "20px" }}>
                No licenses found in the system.
              </td>
            </tr>
          ) : (
            licenses.map((license) => (
              <tr key={license.id}>
                <td>
                  <strong>
                    {license.purchaser
                      ? `${license.purchaser.firstName} ${license.purchaser.lastName}`
                      : "Unassigned"}
                  </strong>
                  <br />
                  <small style={{ color: "#666" }}>
                    {license.purchaser ? license.purchaser.email : license.assignedEmail}
                  </small>
                </td>

                <td>
                  <code style={{ fontSize: "14px", fontWeight: "bold", color: "#214DA0" }}>
                    {license.licenseCode}
                  </code>
                </td>

                <td>
                  <strong>{formatGameName(license.gameName)}</strong>
                </td>

                <td>{license.type || "PC"}</td>

                <td>
                  <div style={{ display: "flex", alignItems: "center", gap: "5px", justifyContent: "center" }}>
                    <span>{license.playsUsed} / </span>
                    <input
                      className="input-admin"
                      type="number"
                      min="1"
                      style={{
                        width: "60px",
                        backgroundColor: editedLicenses[license.id]?.totalPlays !== undefined ? "#c4def8" : "transparent",
                      }}
                      value={license.totalPlays || 10}
                      onChange={(e) =>
                        handleEdit(license.id, "totalPlays", Number(e.target.value))
                      }
                    />
                  </div>
                </td>

                <td>
                  <input
                    className="input-admin"
                    type="date"
                    value={
                      license.expirationDate
                        ? new Date(license.expirationDate).toISOString().split("T")[0]
                        : ""
                    }
                    onChange={(e) =>
                      handleEdit(license.id, "expirationDate", e.target.value)
                    }
                    style={{
                      backgroundColor: editedLicenses[license.id]?.expirationDate ? "#c4def8" : "transparent",
                    }}
                  />
                </td>

                <td>
                  <span
                    style={{
                      padding: "4px 10px",
                      borderRadius: "12px",
                      fontSize: "12px",
                      fontWeight: "bold",
                      backgroundColor: license.active ? "#def7ec" : "#fde8e8",
                      color: license.active ? "#03543f" : "#9b1c1c",
                    }}
                  >
                    {license.active ? "● Active" : "○ Inactive"}
                  </span>
                </td>

                <td>
                  <button
                    onClick={() => handleToggleStatus(license.id, license.active)}
                    disabled={actionLoading[license.id]}
                    style={{
                      padding: "6px 12px",
                      borderRadius: "4px",
                      border: "none",
                      cursor: "pointer",
                      fontWeight: "bold",
                      fontSize: "12px",
                      backgroundColor: license.active ? "#e53e3e" : "#38a169",
                      color: "#fff",
                    }}
                  >
                    {actionLoading[license.id]
                      ? "Updating..."
                      : license.active
                      ? "Deactivate"
                      : "Activate"}
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default LicenseTable;
